/** TinyMCE 표 테두리 모드: solid | frame | guide */
export const TINYMCE_TABLE_BORDER_MODE_ATTR = 'data-tinymce-table-border';


const TABLE_BORDER_STYLE_KEYWORDS = ['none', 'hidden', 'dotted', 'dashed', 'solid', 'double', 'groove', 'ridge', 'inset', 'outset'];

export function parseInlineStyle(el) {
  const style = el.getAttribute?.('style') || '';
  const obj = {};
  style.split(';').forEach(rule => {
    const idx = rule.indexOf(':');
    if (idx === -1) return;
    const k = rule.slice(0, idx).trim().toLowerCase();
    const v = rule.slice(idx + 1).trim();
    if (k && v) obj[k] = v;
  });
  return obj;
}

export function styleObjToString(obj) {
  return Object.entries(obj)
    .map(([k, v]) => `${k}: ${v}`)
    .join('; ');
}

export function extractBorderStyleKeyword(styleObj) {
  if (styleObj['border-style']) {
    const v = styleObj['border-style'].toLowerCase().split(/\s+/)[0];
    if (TABLE_BORDER_STYLE_KEYWORDS.includes(v)) return v;
  }
  if (styleObj['border']) {
    const m = styleObj['border'].toLowerCase().match(/\b(none|hidden|dotted|dashed|solid|double|groove|ridge|inset|outset)\b/);
    if (m) return m[1];
  }
  return null;
}

export function getTableBorderMode(table) {
  const mode = table.getAttribute?.(TINYMCE_TABLE_BORDER_MODE_ATTR);
  if (mode === 'solid' || mode === 'frame' || mode === 'guide') return mode;
  return null;
}

export function setTableBorderMode(table, mode) {
  if (mode === 'solid' || mode === 'frame' || mode === 'guide') {
    table.setAttribute(TINYMCE_TABLE_BORDER_MODE_ATTR, mode);
  } else {
    table.removeAttribute(TINYMCE_TABLE_BORDER_MODE_ATTR);
  }

}

export function hasVisibleTableBorderAttribute(table) {
  const borderAttr = table.getAttribute?.('border');
  if (borderAttr == null || borderAttr === '') return false;
  return String(borderAttr).trim() !== '0';
}

export function isTinyMceTable(table) {
  return table.classList?.contains('mce-item-table');
}

export function hasExplicitCellBorder(table) {
  return Array.from(table.querySelectorAll('td, th')).some(cell => {
    if (cell.hasAttribute?.('border')) return true;
    const style = (cell.getAttribute?.('style') || '').toLowerCase();
    return /(?:^|;)\s*border(?:-[a-z]+)?\s*:/.test(style);
  });
}

/** 셀에 dashed/dotted 이 있으면 frame(외곽 실선·내부 점선) 표 */
export function isFrameBorderTable(table) {
  return Array.from(table.querySelectorAll('td, th')).some(cell => {
    const style = parseInlineStyle(cell);
    for (const key of ['border-style', 'border-top-style', 'border-right-style', 'border-bottom-style', 'border-left-style']) {
      const v = (style[key] || '').toLowerCase();
      if (v === 'dashed' || v === 'dotted') return true;
    }
    const combined = (style['border'] || '').toLowerCase();
    return /\b(dashed|dotted)\b/.test(combined);
  });
}

export function hasSolidBorderOnAllCells(table) {
  const cells = Array.from(table.querySelectorAll('td, th'));
  if (cells.length === 0) return false;
  return cells.every(cell => {
    const kw = extractBorderStyleKeyword(parseInlineStyle(cell));
    return kw === 'solid';
  });
}

export function normalizeSolidCellBorders(table) {
  table.querySelectorAll('td, th').forEach(cell => {
    const cellStyleObj = parseInlineStyle(cell);
    cellStyleObj['border-width'] = cellStyleObj['border-width'] || '1px';
    cellStyleObj['border-style'] = 'solid';
    cellStyleObj['border-color'] = cellStyleObj['border-color'] || '#ccc';
    if (cell.hasAttribute('border')) cell.removeAttribute('border');
    cell.setAttribute('style', styleObjToString(cellStyleObj));
  });
}

export function normalizeOuterSolidInnerDashed(table) {
  const rows = Array.from(table.querySelectorAll('tr'));
  rows.forEach((row, rowIndex) => {
    const cells = Array.from(row.querySelectorAll('th, td'));
    cells.forEach((cell, cellIndex) => {
      const cellStyleObj = parseInlineStyle(cell);
      cellStyleObj['border-width'] = cellStyleObj['border-width'] || '1px';
      cellStyleObj['border-color'] = cellStyleObj['border-color'] || '#ccc';
      cellStyleObj['border-style'] = 'dashed';

      if (rowIndex === 0) cellStyleObj['border-top-style'] = 'solid';
      if (rowIndex === rows.length - 1) cellStyleObj['border-bottom-style'] = 'solid';
      if (cellIndex === 0) cellStyleObj['border-left-style'] = 'solid';
      if (cellIndex === cells.length - 1) cellStyleObj['border-right-style'] = 'solid';

      if (cell.hasAttribute('border')) cell.removeAttribute('border');
      cell.setAttribute('style', styleObjToString(cellStyleObj));
    });
  });
}

/**
 * border="0" + mce-item-table 모드 추론
 * - 표에 border-style: solid 등이 있으면 guide 속성보다 우선
 *   · 행 3개 이상 → 전체 실선(solid)
 *   · 행 2개 이하 → 외곽 실선·내부 점선(frame)
 * - 표·셀 모두 무테두리 + guide → 전체 점선 가이드(guide)
 */
export function resolveTinyMceBorderZeroMode(table, savedMode, tableBorderStyle) {
  if (tableBorderStyle && tableBorderStyle !== 'none' && tableBorderStyle !== 'hidden') {
    const rowCount = table.querySelectorAll('tr').length;
    if (tableBorderStyle === 'solid') {
      return rowCount > 2 ? 'solid' : 'frame';
    }
    return rowCount > 2 ? 'solid' : 'frame';
  }

  if (savedMode === 'solid' || savedMode === 'frame') return savedMode;
  if (isFrameBorderTable(table)) return 'frame';
  if (hasSolidBorderOnAllCells(table)) return 'solid';
  if (hasExplicitCellBorder(table)) return 'frame';
  return 'guide';
}

export function stripGuideTableCellBorderInline(table) {
  table.querySelectorAll('td, th').forEach(cell => {
    const cellStyleObj = parseInlineStyle(cell);
    delete cellStyleObj['border'];
    delete cellStyleObj['border-style'];
    delete cellStyleObj['border-width'];
    delete cellStyleObj['border-color'];
    delete cellStyleObj['border-top-style'];
    delete cellStyleObj['border-right-style'];
    delete cellStyleObj['border-bottom-style'];
    delete cellStyleObj['border-left-style'];
    if (cell.hasAttribute('border')) cell.removeAttribute('border');
    const next = styleObjToString(cellStyleObj);
    if (next) cell.setAttribute('style', next);
    else cell.removeAttribute('style');
  });
}

/**
 * 표 테두리 모드 결정 및 DOM 반영 (에디터·뷰어 공통)
 * @returns {'solid'|'frame'|'guide'}
 */
export function applyTableBorderPresentation(table) {
  const tableStyleObj = parseInlineStyle(table);
  const tableBorderStyle = extractBorderStyleKeyword(tableStyleObj);
  let mode = getTableBorderMode(table);

  if (hasVisibleTableBorderAttribute(table)) {
    table.classList.remove('tinymce-viewer-table-guide');
    if (!mode) mode = isFrameBorderTable(table) ? 'frame' : 'solid';

    if (mode === 'frame') {
      normalizeOuterSolidInnerDashed(table);
    } else if (tableBorderStyle) {
      table.querySelectorAll('td, th').forEach(cell => {
        const cellStyleObj = parseInlineStyle(cell);
        if (cellStyleObj['border']) {
          cellStyleObj['border'] = cellStyleObj['border'].replace(
            /\b(none|hidden|dotted|dashed|solid|double|groove|ridge|inset|outset)\b/i,
            tableBorderStyle,
          );
        }
        cellStyleObj['border-style'] = tableBorderStyle;
        cell.setAttribute('style', styleObjToString(cellStyleObj));
      });
      mode = 'solid';
    } else if (hasSolidBorderOnAllCells(table)) {
      normalizeSolidCellBorders(table);
      mode = 'solid';
    } else if (isFrameBorderTable(table)) {
      normalizeOuterSolidInnerDashed(table);
      mode = 'frame';
    }

    setTableBorderMode(table, mode || 'solid');
    return mode || 'solid';
  }

  if (isTinyMceTable(table)) {
    table.classList.remove('tinymce-viewer-table-guide');
    const borderAttr = table.getAttribute('border');
    if (borderAttr != null && String(borderAttr).trim() === '0') {
      table.setAttribute('border', '0');
    }

    mode = resolveTinyMceBorderZeroMode(table, mode, tableBorderStyle);

    if (mode === 'solid') {
      normalizeSolidCellBorders(table);
    } else if (mode === 'frame') {
      normalizeOuterSolidInnerDashed(table);
    } else {
      stripGuideTableCellBorderInline(table);
    }

    setTableBorderMode(table, mode);
    return mode;
  }

  const isBorderlessGuide = !tableBorderStyle || tableBorderStyle === 'none' || tableBorderStyle === 'hidden';

  if (isBorderlessGuide) {
    table.classList.add('tinymce-viewer-table-guide');
    stripGuideTableCellBorderInline(table);
    setTableBorderMode(table, 'guide');
    return 'guide';
  }

  table.classList.remove('tinymce-viewer-table-guide');
  if (!mode) mode = isFrameBorderTable(table) ? 'frame' : 'solid';

  if (mode === 'frame') {
    normalizeOuterSolidInnerDashed(table);
  } else {
    table.querySelectorAll('td, th').forEach(cell => {
      const cellStyleObj = parseInlineStyle(cell);
      if (cellStyleObj['border']) {
        cellStyleObj['border'] = cellStyleObj['border'].replace(
          /\b(none|hidden|dotted|dashed|solid|double|groove|ridge|inset|outset)\b/i,
          tableBorderStyle,
        );
      }
      cellStyleObj['border-style'] = tableBorderStyle;
      cell.setAttribute('style', styleObjToString(cellStyleObj));
    });
  }

  setTableBorderMode(table, mode);
  return mode;
}

export function processAllTablesBorder(root) {
  if (!root) return;
  root.querySelectorAll('table').forEach(table => applyTableBorderPresentation(table));
}

